import { Route } from 'vue-router';
import { StoreModel } from '@/models/store';
import { EventBus } from '@/eventBus';
import { usePermission } from '@/services/PermissionService';
import Vue from 'vue';

// Declare the $appStore property on Vue prototype
declare module 'vue/types/vue' {
  interface VueConstructor {
    prototype: {
      $permissionService?: any;
      $appStore?: StoreModel;
      $permissionsInitializing?: boolean;
      $root: any;
    };
  }
}

// Track routes accessed during initialization to recheck later
export const pendingPermissionChecks: Array<{
  requiredPermission: string,
  to: Route,
}> = [];

/**
 * General function to check if a permission is granted
 * This handles all special cases in one place to keep the logic consistent
 */
export function checkPermissionAccess(requiredPermission: string, to: Route): boolean {
  // Try permission service first
  try {
    const permissionService = usePermission();
    const hasPermission = permissionService.hasPermission(requiredPermission);
    return hasPermission;
  } catch (error) {
    // add route to pending checks
    if (to.name) {
      pendingPermissionChecks.push({
        to,
        requiredPermission,

      });
    } else {
      // no-op
    }
    return true;
  }
  return false;
}

/**
 * Route guard to check permissions
 * Prevents navigation if user doesn't have permission
 */
export function permissionGuard(to: Route, _from: Route, next: any): void {
  // Skip permission check for routes that don't require it
  if (!to.meta || !to.meta.requiredPermission) {
    next();
    return;
  }

  // Always log permission checks for debugging
  const requiredPermission = String(to.meta.requiredPermission);

  // Allow navigation during app initialization to prevent blocking on first load
  if (Vue.prototype.$permissionsInitializing === true) {

    // Add to pending checks
    pendingPermissionChecks.push({
      to,
      requiredPermission,
    });

    next();
    return;
  }

  // Use the general permission check function
  const hasPermission = checkPermissionAccess(requiredPermission, to);

  if (hasPermission) {
    next();
  } else {
    redirectToPermissionDenied();
  }

  // Helper function to handle permission denied redirects
  function redirectToPermissionDenied() {

    next({
      path: '/permission-denied',
      query: {
        feature: requiredPermission,
        redirect: to.fullPath,
      },
    });

    // Type-safe version of the event emission
    const routePath = typeof to.path === 'string' ? to.path : '';
    EventBus.$emit('permission-denied', {
      feature: requiredPermission,
      route: routePath,
    });
  }
}
